import {
  FC,
  ReactNode,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
} from 'react';
import {
  ImperativePanelHandle,
  Panel,
  PanelProps,
} from 'react-resizable-panels';
import { useDockableGroupContext } from './WbDockableGroup';
import { DockableCardHeader } from './WbDockableCardHeader';
import { WbCard } from '../WbCard/WbCard';

const COLLAPSED_PANEL_SIZE_PX = 36;
const DEFAULT_COLLAPSED_SIZE_PCT = 3;
const FULLY_EXPANDED_PRIMARY_PANEL_THRESHOLD = 99;
const RESIZE_HANDLE_GUTTER_PX = 12;

export type WbDockablePanelHandle = ImperativePanelHandle;

/*
 * Note: This component is designed to be used as a direct child of WbDockableGroup and relies on context provided by it to manage its state and behavior as a collapsible panel within the group.
 * It should not be used outside of this context or without being wrapped in a WbDockableGroup.
 */

export const WbDockablePanel: FC<
  PanelProps & {
    isCardChildren?: boolean;
    showCollapsedContent?: boolean;
    primary?: boolean;
    title?: string;
    index?: number;
    noHeader?: boolean;
    headerActions?: ReactNode;
    preview?: ReactNode;
    headerBorderBottom?: boolean;
    cardWrapper?: boolean;
    cardHeaderStyles?: React.CSSProperties;
  }
> = ({
  title,
  style,
  headerBorderBottom,
  cardWrapper = false,
  isCardChildren = false,
  collapsible = true,
  showCollapsedContent = false,
  noHeader,
  headerActions,
  preview,
  onCollapse,
  onResize,
  children,
  primary,
  index,
  cardHeaderStyles,
  ...panelProps
}) => {
  const ctx = useDockableGroupContext();
  const innerRef = useRef<WbDockablePanelHandle>(null);
  const isMobile = ctx?.isMobile ?? false;

  const currentPanelIdx = index !== undefined ? index : 0;
  const isPrimary =
    primary !== undefined ? primary : ctx?.primaryIndex === currentPanelIdx;
  const isCollapsed = isPrimary ? ctx?.isPrimaryCollapsed ?? false : false;
  const isPrimaryFullyExpanded = ctx?.isPrimaryFullyExpanded ?? false;
  const isHorizontalLayout = (ctx?.direction ?? 'horizontal') === 'horizontal';
  const primaryIndex = ctx?.primaryIndex ?? 0;
  const contentRef = useRef<HTMLDivElement>(null);

  let handleGutterStyle: React.CSSProperties | undefined;
  if (isPrimary && isPrimaryFullyExpanded && !isCollapsed) {
    if (isHorizontalLayout) {
      handleGutterStyle =
        primaryIndex === 0
          ? { paddingRight: RESIZE_HANDLE_GUTTER_PX }
          : { paddingLeft: RESIZE_HANDLE_GUTTER_PX };
    } else {
      handleGutterStyle =
        primaryIndex === 0
          ? { paddingBottom: RESIZE_HANDLE_GUTTER_PX }
          : { paddingTop: RESIZE_HANDLE_GUTTER_PX };
    }
  }

  // Register panel ref with context on mount and unregister on unmount
  useEffect(() => {
    if (!ctx) return undefined;
    ctx.registerPanel(currentPanelIdx, innerRef.current);
    return () => ctx.registerPanel(currentPanelIdx, null);
  }, [ctx, currentPanelIdx]);

  // On initial mount, if this is the primary panel and it should start collapsed (e.g. on mobile), collapse it immediately to avoid flicker
  useLayoutEffect(() => {
    if (!isPrimary || !isCollapsed) return;
    innerRef.current?.collapse();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Sync collapse state from context to panel.
  const prevIsCollapsed = useRef(isCollapsed);
  useEffect(() => {
    if (!isPrimary) return;
    if (prevIsCollapsed.current === isCollapsed) return;
    prevIsCollapsed.current = isCollapsed;

    if (isCollapsed) {
      innerRef.current?.collapse();
    } else if (!isMobile) {
      innerRef.current?.expand();
    }
  }, [isPrimary, isCollapsed, isMobile]);

  const requestPrimaryCollapsed = useCallback(
    (collapsed: boolean, options?: { respectExpandOnResize?: boolean }) => {
      if (!isPrimary) return;
      const canSync =
        !(options?.respectExpandOnResize ?? false) ||
        collapsed ||
        ctx?.expandOnResize;
      if (canSync && collapsed !== ctx?.isPrimaryCollapsed) {
        ctx?.setPrimaryCollapsed(collapsed);
      }
    },
    [ctx, isPrimary],
  );

  const onPrimaryCollapse = useCallback(
    (collapsed: boolean) => {
      requestPrimaryCollapsed(collapsed, { respectExpandOnResize: true });
      onCollapse?.(collapsed);
    },
    [onCollapse, requestPrimaryCollapsed],
  );

  const onSecondaryCollapse = useCallback(
    (collapsed: boolean) => {
      if (collapsed) contentRef.current?.setAttribute('inert', '');
      else contentRef.current?.removeAttribute('inert');
      onCollapse?.(collapsed);
    },
    [onCollapse],
  );

  const panelSizeOverrides = useMemo((): Partial<PanelProps> => {
    // Configure size constraints for primary and secondary panels
    if (!isPrimary) {
      if (isMobile) {
        return {
          defaultSize: 100 - DEFAULT_COLLAPSED_SIZE_PCT,
          collapsible: true,
          collapsedSize: 0,
          minSize: 0,
          maxSize: 100,
        };
      }
      return {};
    }
    const isHorizontal = ctx?.direction !== 'vertical';

    return {
      // eslint-disable-next-line no-nested-ternary
      defaultSize: isMobile
        ? isCollapsed
          ? DEFAULT_COLLAPSED_SIZE_PCT
          : 100
        : panelProps.defaultSize ?? 30,
      collapsedSize: DEFAULT_COLLAPSED_SIZE_PCT,
      style: isCollapsed
        ? {
            flexGrow: 0,
            flexShrink: 0,
            flexBasis: `${COLLAPSED_PANEL_SIZE_PX}px`,
            // EDGE CASE 3: Force inline dimension constraints to prevent micro-flicker with the library
            maxWidth: isHorizontal ? `${COLLAPSED_PANEL_SIZE_PX}px` : undefined,
            maxHeight: !isHorizontal
              ? `${COLLAPSED_PANEL_SIZE_PX}px`
              : undefined,
            minWidth: isHorizontal ? `${COLLAPSED_PANEL_SIZE_PX}px` : undefined,
            minHeight: !isHorizontal
              ? `${COLLAPSED_PANEL_SIZE_PX}px`
              : undefined,
          }
        : {},
      minSize: panelProps.minSize ?? 15,
      maxSize: 100,
    };
  }, [
    isPrimary,
    ctx?.direction,
    isMobile,
    isCollapsed,
    panelProps.defaultSize,
    panelProps.minSize,
  ]);

  const onPrimaryResize = useCallback(
    (size: number, prevSize: number) => {
      const isFullyExpanded = size >= FULLY_EXPANDED_PRIMARY_PANEL_THRESHOLD;
      if (ctx && ctx.isPrimaryFullyExpanded !== isFullyExpanded) {
        ctx.setPrimaryFullyExpanded(isFullyExpanded);
      }
      // EDGE CASE 1: Use tolerance threshold. If panel grows significantly beyond collapse point (even with fast mouse movements),
      // force expansion and update state
      const isExpandingFromCollapsed =
        isCollapsed && size > DEFAULT_COLLAPSED_SIZE_PCT + 1.5;

      if (isPrimary && isExpandingFromCollapsed) {
        requestPrimaryCollapsed(false, { respectExpandOnResize: true });
      }
      onResize?.(size, prevSize);
    },
    [ctx, isPrimary, isCollapsed, onResize, requestPrimaryCollapsed],
  );

  useEffect(() => {
    if (!isMobile || !innerRef.current) return;
    if (isPrimary) {
      innerRef.current.resize(DEFAULT_COLLAPSED_SIZE_PCT);
      innerRef.current.collapse();
    } else {
      innerRef.current.resize(100 - DEFAULT_COLLAPSED_SIZE_PCT);
    }
  }, [isMobile, isPrimary]);

  const primaryPanelToggle = useMemo(() => {
    if (!isPrimary) return undefined;
    const position =
      ctx?.primaryIndex !== undefined && ctx.primaryIndex > 0 ? 'end' : 'start';
    return {
      direction: ctx?.direction ?? 'horizontal',
      position: position as 'start' | 'end',
    };
  }, [isPrimary, ctx?.direction, ctx?.primaryIndex]);

  const showHeader =
    noHeader === false ||
    (!noHeader && Boolean(title || headerActions)) ||
    isPrimary;

  const contentLayout = (
    <>
      {showHeader && (
        <DockableCardHeader
          cardHeaderStyles={cardHeaderStyles}
          title={title}
          actions={headerActions}
          borderBottom={headerBorderBottom}
          toggle={
            isPrimary
              ? {
                  isCollapsed,
                  direction: primaryPanelToggle?.direction,
                  position: primaryPanelToggle?.position,
                  onToggle: () => requestPrimaryCollapsed(!isCollapsed),
                }
              : undefined
          }
        />
      )}
      {isPrimary && isCollapsed && preview && (
        <div style={{ width: '100%', overflow: 'hidden' }}>{preview}</div>
      )}
      {(showCollapsedContent || !isCollapsed) && (
        <div
          ref={isPrimary ? undefined : contentRef}
          {...(isCollapsed && { inert: '' })}
          style={{
            flex: 1,
            minHeight: 0,
            minWidth: 0,
            overflow: 'auto',
            opacity: isCollapsed ? 0 : 1,
            pointerEvents: isCollapsed ? 'none' : 'auto',
            transition: 'opacity 350ms ease',
          }}
        >
          {children}
        </div>
      )}
    </>
  );

  return (
    <Panel
      ref={innerRef}
      collapsible={collapsible}
      {...panelProps}
      {...panelSizeOverrides}
      onCollapse={isPrimary ? onPrimaryCollapse : onSecondaryCollapse}
      onResize={isPrimary ? onPrimaryResize : onResize}
      style={{
        ...panelSizeOverrides.style,
        ...style,

        overflow: isCollapsed ? 'hidden' : style?.overflow ?? 'auto',
      }}
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          minHeight: 0,
          minWidth: 0,
          overflowX: isCollapsed ? 'hidden' : 'auto',
          overflowY: isMobile ? 'auto' : 'hidden',
          alignItems: isCollapsed ? 'center' : undefined,
          justifyContent: isCollapsed ? 'flex-start' : undefined,
          ...handleGutterStyle,
        }}
      >
        {cardWrapper ? (
          <WbCard cardStyle={{ height: '100%' }}>{contentLayout}</WbCard>
        ) : (
          contentLayout
        )}
      </div>
    </Panel>
  );
};
